// src/components/proposals/LeadTemplate.jsx
//
// The Lead pre-proposal — a short document sent BEFORE a full proposal, to find
// out which tier a prospect is actually interested in.
//
// Same visual language as Bronze/Gold (dark vignette cover, teal accent, the
// same header/footer chrome, all imported from proposalChrome.jsx), but a
// different SHAPE of content: a comparison across all four tiers rather than
// one tier's narrative. Nothing here is customer-specific except the name,
// contact and date on the cover — the rest is the shared service catalog held
// by the Lead template.
//
// EDITING happens in the settings pane, not inline. Only the handful of
// customer fields on the cover and the hero copy are inline-editable; a matrix
// of 21 features across 4 tiers is not something you edit by clicking into a
// printed table, which is why LeadSettingsPanel.jsx exists.
//
// LAYOUT IS TABLES, NOT CSS GRID — Prince (DocRaptor's engine) implements
// neither grid nor flex `gap`. See proposalChrome.jsx.
import React from "react";
import {
  PAGE_BASE, PAGE_BODY_H, CoverPage, HeaderBar, GhostNumber, PageFooter,
  SectionLabel, SectionBand, SplitRow, BulletRow, dot, F, EmptyBlock,
} from "./proposalChrome";
import { packSegments } from "./pagePacking";
import {
  DEFAULT_LEAD_CONTENT, LEAD_TIER_KEYS, LEAD_TIER_LABELS, isTextRow,
  featureMatrixSegments, MATRIX_CAPACITY, MATRIX_FIRST_OFFSET, MATRIX_CONT_OFFSET,
  LEAD_INTRO_H, LEAD_CONT_H, ADDONS_HEAD_H, ADDONS_NOTE_H,
  estimateAddOnRowHeight, estimateTermHeight,
} from "./leadContent";

const DOC_LABEL = "Service Plans Overview";

/* Alternating column tint, and the emphasis Gold carries in the source design.
   Keyed by tier so a reordered column can never take another column's shading. */
const TIER_CELL_BG = {
  "copper-defender": "#fafafa",
  "bronze-sentinel": null,
  "silver-guardian": "#fafafa",
  "gold-fortress":   "#f0f9ff",
};

const TIER_HEAD_COLOR = {
  "copper-defender": "rgba(255,255,255,0.65)",
  "bronze-sentinel": "rgba(255,255,255,0.65)",
  "silver-guardian": "rgba(255,255,255,0.65)",
  "gold-fortress":   "#0b80a5",
};

/* Rates are entered as free text (see LeadSettingsPanel — a numeric input
   seeded with a literal 0 is what produced the `010` bug in the Foundational
   editor), so a rate can arrive as a number, a numeric string, or something
   Craig typed like "POA". A number gets the dollar sign and the unit; anything
   else is printed exactly as written, because guessing at it is how "POA"
   becomes "$NaN" on a customer document. */
function rateText(value, unit) {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(value);
  const looksNumeric = Number.isFinite(n) && String(value).trim() === String(n);
  return looksNumeric ? `$${n}${unit}` : String(value);
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 2 — Introduction
   ══════════════════════════════════════════════════════════════════════ */
function HeroPage({ c, mode, set }) {
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
      <GhostNumber n="01" />
      <div style={{ padding: "44px 80px 44px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 01 — Introduction" />

        <h1 style={{ fontSize: 34, fontWeight: 800, color: "#1a1a1a", margin: "0 0 28px",
                     letterSpacing: "-0.03em", lineHeight: 1.15 }}>
          <F mode={mode} value={c.heroHeading} onChange={(v) => set({ heroHeading: v })}
             placeholder="Headline…"
             style={{ fontSize: 34, fontWeight: 800, color: "#1a1a1a",
                      letterSpacing: "-0.03em", lineHeight: 1.15 }} />
        </h1>

        <div style={{ width: 56, height: 3, background: "#0b80a5", marginBottom: 28 }} />

        <p style={{ fontSize: 15, lineHeight: 1.85, color: "#1f2937", margin: 0 }}>
          <F mode={mode} value={c.heroBody} onChange={(v) => set({ heroBody: v })}
             placeholder="Introduction…" multiline rows={9}
             style={{ fontSize: 15, lineHeight: 1.85, color: "#1f2937" }} />
        </p>

        <div style={{ marginTop: 48, paddingTop: 28, borderTop: "1px solid #f0f0f0" }}>
          <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 14px" }}>
            Prepared for {c.customerName || "your business"} by
          </p>
          <p style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a", margin: "0 0 4px",
                      letterSpacing: "-0.02em" }}>{c.salesRepName}</p>
          <p style={{ fontSize: 13, color: "#4b5563", margin: 0, fontWeight: 500 }}>{c.salesRepTitle}</p>
          <p style={{ fontSize: 13, color: "#0b80a5", margin: "3px 0 0", fontWeight: 600 }}>
            Datatech Solutions
          </p>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 3 — Service plans
   Four full-width cards, one per tier, each carrying its blurb and rates.
   ══════════════════════════════════════════════════════════════════════ */
function TierCard({ tier, emphasis }) {
  const perDevice = rateText(tier.perDeviceRate, "/device/mth");
  const perUser   = rateText(tier.perUserRate, "/user/mth");

  return (
    <div style={{ border: `1px solid ${emphasis ? "rgba(11,128,165,0.35)" : "#e5e9ef"}`,
                  borderLeft: `4px solid ${emphasis ? "#0b80a5" : "#1a1a1a"}`,
                  background: emphasis ? "#f0f9ff" : "#f8fafc",
                  borderRadius: 8, padding: "16px 20px", marginBottom: 12 }}>
      <SplitRow
        align="top"
        style={{ marginBottom: 8 }}
        left={
          <h3 style={{ fontSize: 17, fontWeight: 800, color: "#1a1a1a", margin: 0,
                       letterSpacing: "-0.02em" }}>{tier.name}</h3>
        }
        right={
          <div style={{ textAlign: "right" }}>
            {perDevice && (
              <p style={{ fontSize: 17, fontWeight: 800, color: "#0b80a5", margin: 0,
                          letterSpacing: "-0.02em", whiteSpace: "nowrap" }}>{perDevice}</p>
            )}
            {perUser ? (
              <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", margin: "2px 0 0",
                          whiteSpace: "nowrap" }}>+ {perUser}</p>
            ) : (
              <p style={{ fontSize: 10.5, color: "#6b7280", margin: "3px 0 0", fontStyle: "italic",
                          whiteSpace: "nowrap" }}>Device-based pricing — no per-user fee</p>
            )}
          </div>
        }
      />
      <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, margin: 0 }}>{tier.blurb}</p>
    </div>
  );
}

function TierOverviewPage({ c }) {
  const tiers = Array.isArray(c.tiers) ? c.tiers : [];
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
      <GhostNumber n="02" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 02" />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Our Service Plans</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 22px", lineHeight: 1.5 }}>
          Four tiers of managed IT support. Every plan builds on the one below it, so you
          can start where it makes sense and grow your coverage as your business does.
        </p>

        {tiers.length === 0 ? (
          <EmptyBlock title="No service plans configured"
                      hint="Open the settings panel → Service plans → Add plan" pad={40} />
        ) : tiers.map((tier, i) => (
          <TierCard key={tier.key || i} tier={tier} emphasis={tier.key === "gold-fortress"} />
        ))}

        <p style={{ fontSize: 11.5, color: "#4b5563", margin: "10px 0 0", fontStyle: "italic" }}>
          All prices are exclusive of GST. Bronze Sentinel, Silver Guardian and Gold Fortress
          require a Microsoft 365 Business Premium licence.
        </p>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 4 — Feature comparison
   The only Lead section that routinely spans several pages. Rows are packed
   into as many fixed-height page divs as they need — the same "one div = one
   Prince page" pattern every other page uses, just repeated — and each page
   carries its own header bar and its own copy of the table head, so the
   column headings repeat without needing Prince running-elements.
   ══════════════════════════════════════════════════════════════════════ */
function MatrixHead({ tiers }) {
  return (
    <thead>
      <tr>
        <th style={{ textAlign: "left", padding: "12px 14px 12px 16px", background: "#1a1a1a",
                     color: "rgba(255,255,255,0.6)", fontWeight: 500, fontSize: 11,
                     width: "42%", borderBottom: "3px solid #0b80a5" }}>Feature</th>
        {LEAD_TIER_KEYS.map((key) => {
          const tier = tiers.find((x) => x.key === key);
          const perDevice = rateText(tier?.perDeviceRate, "/dev");
          const perUser   = rateText(tier?.perUserRate, "/user");
          return (
            <th key={key} style={{ textAlign: "center", padding: "10px 6px",
                                   background: "#1a1a1a", width: "14.5%",
                                   borderBottom: "3px solid #0b80a5" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: TIER_HEAD_COLOR[key],
                            letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 3 }}>
                {LEAD_TIER_LABELS[key]}
              </div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", fontWeight: 500,
                            lineHeight: 1.35 }}>
                {perDevice}{perUser ? <> + {perUser}</> : null}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

/* One cell. A boolean row renders a teal tick or a grey dash; a textValues row
   renders its string directly. Detecting the shape per ROW rather than per cell
   keeps a half-filled row from mixing the two. */
function MatrixCell({ feature, tierKey }) {
  const style = { textAlign: "center", padding: "7px 6px", verticalAlign: "middle",
                  background: TIER_CELL_BG[tierKey] || undefined };
  if (isTextRow(feature)) {
    const text = feature.textValues?.[tierKey] || "";
    return (
      <td style={style}>
        {text
          ? <span style={{ fontSize: 9.5, fontWeight: 600, color: "#1f2937",
                           lineHeight: 1.35, display: "inline-block" }}>{text}</span>
          : <span style={{ color: "#e5e7eb", fontSize: 13 }}>—</span>}
      </td>
    );
  }
  return (
    <td style={style}>
      {feature?.tiers?.[tierKey]
        ? <span style={{ color: "#0b80a5", fontWeight: 800, fontSize: 14 }}>✓</span>
        : <span style={{ color: "#e5e7eb", fontSize: 13 }}>—</span>}
    </td>
  );
}

function FeatureMatrixPages({ c }) {
  const sections = Array.isArray(c.featureSections) ? c.featureSections : [];
  const tiers    = Array.isArray(c.tiers) ? c.tiers : [];
  const segments = featureMatrixSegments(sections);
  const pages = segments.length
    ? packSegments(segments, { firstOffset: MATRIX_FIRST_OFFSET,
                               contOffset:  MATRIX_CONT_OFFSET,
                               capacity:    MATRIX_CAPACITY })
    : [[]];

  return (
    <>
      {pages.map((pageSegs, pageIdx) => (
        <div key={pageIdx} className="page" data-flow="lead-matrix" style={PAGE_BASE}>
          <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
          <GhostNumber n={pageIdx === 0 ? "03" : `03.${pageIdx + 1}`} />
          <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
            {pageIdx === 0 ? (
              <>
                <SectionLabel text="Section 03" />
                <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                             letterSpacing: "-0.02em" }}>What's Included</h2>
                <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
                  Every service across all four plans, side by side.
                </p>
              </>
            ) : (
              <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                What's Included (continued)
              </p>
            )}

            {segments.length === 0 ? (
              <EmptyBlock title="No features added yet"
                          hint="Open the settings panel → Feature comparison → Add feature"
                          pad={40} />
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5,
                              tableLayout: "fixed" }}>
                <MatrixHead tiers={tiers} />
                <tbody>
                  {pageSegs.map((s) => s.kind === "section" ? (
                    <tr key={s.key}>
                      <td colSpan={1 + LEAD_TIER_KEYS.length} style={{ padding: 0 }}>
                        <SectionBand title={s.section.title} />
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "7px 14px 7px 16px", verticalAlign: "top" }}>
                        <div style={{ fontSize: 12.5, color: "#1f2937", fontWeight: 600,
                                      lineHeight: 1.35 }}>{s.feature.name}</div>
                        {s.feature.description && (
                          <div style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.45,
                                        marginTop: 2 }}>{s.feature.description}</div>
                        )}
                      </td>
                      {LEAD_TIER_KEYS.map((key) => (
                        <MatrixCell key={key} feature={s.feature} tierKey={key} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <PageFooter />
        </div>
      ))}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 5 — Optional add-ons
   Prices are STRINGS ("$50" / "Included" / ""), printed verbatim — see
   leadContent.js for why they are not numbers.
   ══════════════════════════════════════════════════════════════════════ */
function AddOnsPages({ c }) {
  const addOns = Array.isArray(c.addOns) ? c.addOns : [];
  const segments = addOns.map((addOn, i) => ({
    key: `addon-${i}`, kind: "row", h: estimateAddOnRowHeight(addOn), addOn,
  }));
  if (c.addOnsNote) segments.push({ key: "note", kind: "note", h: ADDONS_NOTE_H });

  const pages = segments.length
    ? packSegments(segments, { firstOffset: LEAD_INTRO_H + ADDONS_HEAD_H,
                               contOffset:  LEAD_CONT_H + ADDONS_HEAD_H })
    : [[]];

  return (
    <>
      {pages.map((pageSegs, pageIdx) => {
        const rows = pageSegs.filter((s) => s.kind === "row");
        const hasNote = pageSegs.some((s) => s.kind === "note");
        return (
          <div key={pageIdx} className="page" data-flow="lead-addons" style={PAGE_BASE}>
            <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
            <GhostNumber n={pageIdx === 0 ? "04" : `04.${pageIdx + 1}`} />
            <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
              {pageIdx === 0 ? (
                <>
                  <SectionLabel text="Section 04" />
                  <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                               letterSpacing: "-0.02em" }}>Optional Add-Ons</h2>
                  <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
                    Add any of these to any plan. Prices are per month, exclusive of GST.
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                  Optional Add-Ons (continued)
                </p>
              )}

              {addOns.length === 0 ? (
                <EmptyBlock title="No add-ons added yet"
                            hint="Open the settings panel → Optional add-ons → Add add-on"
                            pad={40} />
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5,
                                tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "10px 14px 10px 16px",
                                   background: "#1a1a1a", color: "rgba(255,255,255,0.6)",
                                   fontWeight: 500, fontSize: 11, width: "42%",
                                   borderBottom: "3px solid #0b80a5" }}>Add-On</th>
                      {LEAD_TIER_KEYS.map((key) => (
                        <th key={key} style={{ textAlign: "center", padding: "10px 6px",
                                               background: "#1a1a1a", width: "14.5%",
                                               borderBottom: "3px solid #0b80a5",
                                               fontSize: 10, fontWeight: 700,
                                               color: TIER_HEAD_COLOR[key],
                                               letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          {LEAD_TIER_LABELS[key]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "7px 14px 7px 16px", verticalAlign: "top" }}>
                          <div style={{ fontSize: 12.5, color: "#1f2937", fontWeight: 600,
                                        lineHeight: 1.35 }}>{s.addOn.name}</div>
                          {s.addOn.description && (
                            <div style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.45,
                                          marginTop: 2 }}>{s.addOn.description}</div>
                          )}
                          {s.addOn.unit && (
                            <div style={{ fontSize: 10.5, color: "#9ca3af", lineHeight: 1.45,
                                          marginTop: 2, fontStyle: "italic" }}>{s.addOn.unit}</div>
                          )}
                        </td>
                        {LEAD_TIER_KEYS.map((key) => {
                          const value = s.addOn.pricing?.[key] || "";
                          const included = /^included$/i.test(String(value).trim());
                          return (
                            <td key={key} style={{ textAlign: "center", padding: "7px 6px",
                                                   verticalAlign: "middle",
                                                   background: TIER_CELL_BG[key] || undefined }}>
                              {value
                                ? <span style={{ fontSize: included ? 9.5 : 12.5,
                                                 fontWeight: 700,
                                                 color: included ? "#0b80a5" : "#1a1a1a",
                                                 textTransform: included ? "uppercase" : "none",
                                                 letterSpacing: included ? "0.05em" : "normal" }}>
                                    {value}
                                  </span>
                                : <span style={{ color: "#e5e7eb", fontSize: 13 }}>—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {hasNote && (
                <p style={{ fontSize: 11.5, color: "#4b5563", margin: "16px 0 0",
                            lineHeight: 1.6, fontStyle: "italic" }}>
                  {c.addOnsNote}
                </p>
              )}
            </div>
            <PageFooter />
          </div>
        );
      })}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 6 — Terms & conditions
   An editable list, so it is packed rather than assumed to fit: a term added
   past the bottom of a fixed page would be silently clipped, not reflowed.
   ══════════════════════════════════════════════════════════════════════ */
function TermsPages({ c }) {
  const terms = (Array.isArray(c.termsAndConditions) ? c.termsAndConditions : [])
    .filter((t) => String(t || "").trim());
  const segments = terms.map((term, i) => ({
    key: `term-${i}`, h: estimateTermHeight(term), term,
  }));
  const pages = segments.length
    ? packSegments(segments, { firstOffset: LEAD_INTRO_H + 30, contOffset: LEAD_CONT_H + 30 })
    : [[]];

  return (
    <>
      {pages.map((pageSegs, pageIdx) => (
        <div key={pageIdx} className="page" data-flow="lead-terms" style={PAGE_BASE}>
          <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
          <GhostNumber n={pageIdx === 0 ? "05" : `05.${pageIdx + 1}`} />
          <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
            {pageIdx === 0 ? (
              <>
                <SectionLabel text="Section 05" />
                <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                             letterSpacing: "-0.02em" }}>Terms &amp; Conditions</h2>
                <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
                  The terms that apply to every plan above.
                </p>
              </>
            ) : (
              <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                Terms &amp; Conditions (continued)
              </p>
            )}

            {terms.length === 0 ? (
              <EmptyBlock title="No terms added yet"
                          hint="Open the settings panel → Terms & conditions → Add term"
                          pad={40} />
            ) : (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <SectionBand title="Basic Terms &amp; Conditions" />
                <div style={{ padding: "14px 20px" }}>
                  {pageSegs.map((s, i) => (
                    <BulletRow key={s.key} bullet={dot()}
                               bottom={i === pageSegs.length - 1 ? 0 : 10}>
                      <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.55, margin: 0 }}>
                        {s.term}
                      </p>
                    </BulletRow>
                  ))}
                </div>
              </div>
            )}
          </div>
          <PageFooter />
        </div>
      ))}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 7 — Response times
   A fixed five-row table. Not packed: the rows are the four SLA priorities
   plus "No Priority", and that set does not grow.
   ══════════════════════════════════════════════════════════════════════ */
const thMuted = {
  fontSize: 10, fontWeight: 700, color: "#6b7280",
  letterSpacing: "0.08em", textTransform: "uppercase",
};

function ResponseTimesPage({ c }) {
  const rows = Array.isArray(c.responseTimes) ? c.responseTimes : [];
  const naCell  = { fontSize: 12, color: "#9ca3af", fontWeight: 600 };
  const valCell = { fontSize: 12.5, color: "#1a1a1a", fontWeight: 700 };
  const cell = (v) => (String(v).trim().toUpperCase() === "N/A" ? naCell : valCell);

  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} docLabel={DOC_LABEL} />
      <GhostNumber n="06" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 06" />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Response Times</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 18px", lineHeight: 1.5 }}>
          First-response guarantees apply on Silver Guardian and Gold Fortress. Copper
          Defender and Bronze Sentinel are supported at our standard hourly rate.
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 14px 8px 0", width: "16%" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a",
                               letterSpacing: "0.08em", textTransform: "uppercase" }}>Priority</span>
                <div style={{ width: 22, height: 2, background: "#0b80a5", marginTop: 4 }} />
              </th>
              <th style={{ textAlign: "left", padding: "8px 14px", width: "42%" }}>
                <span style={thMuted}>Examples</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%",
                           background: "#f8fafc" }}>
                <span style={{ ...thMuted, fontSize: 9.5, lineHeight: 1.3 }}>Copper &amp; Bronze</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%",
                           background: "rgba(11,128,165,0.07)" }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Silver</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%" }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Gold Fortress</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.priority || i}
                  style={{ borderTop: "1px solid #f0f0f0",
                           borderBottom: i === rows.length - 1 ? "1px solid #f0f0f0" : undefined }}>
                <td style={{ padding: "10px 14px 10px 0", verticalAlign: "top" }}>
                  <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                                 background: r.dot || "#9ca3af", marginRight: 7,
                                 verticalAlign: "middle" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a",
                                 verticalAlign: "middle" }}>{r.priority}</span>
                </td>
                <td style={{ padding: "10px 14px", verticalAlign: "top" }}>
                  {(Array.isArray(r.examples) ? r.examples : []).map((ex, j) => (
                    <BulletRow key={j} gap={7} bullet={dot("#d1d5db", 4, 6)}
                               bottom={j === r.examples.length - 1 ? 0 : 4}>
                      <p style={{ fontSize: 11.5, color: "#4b5563", lineHeight: 1.45, margin: 0 }}>
                        {ex}
                      </p>
                    </BulletRow>
                  ))}
                </td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: "#f8fafc", ...cell(r.copperBronze) }}>{r.copperBronze}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: "rgba(11,128,165,0.07)", ...cell(r.silver) }}>{r.silver}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             ...cell(r.gold) }}>{r.gold}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ fontSize: 11.5, color: "#4b5563", margin: "18px 0 0", fontStyle: "italic",
                    lineHeight: 1.6 }}>
          Response times are measured during business hours, Mon–Fri 8:30am–5pm AWST. Requests
          lodged outside those hours are actioned on a best-effort basis.
        </p>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   The template
   ══════════════════════════════════════════════════════════════════════ */
// assetBase: prefix for the logo files in public/proposal-assets. Empty on
// screen (root-relative is fine in the browser). For mode="print" the caller
// MUST pass an absolute origin — DocRaptor fetches images server-side and
// cannot resolve "/proposal-assets/...".
export default function LeadTemplate({ content, mode = "edit", onChange,
                                       assetBase = "", onCoverImage }) {
  const c = { ...DEFAULT_LEAD_CONTENT(), ...(content || {}) };
  const set = React.useCallback(
    (patch) => { if (onChange) onChange(patch); },
    [onChange]
  );

  return (
    <div className="proposal-lead doc-pages" data-mode={mode}>
      <CoverPage
        c={c} mode={mode} set={set} assetBase={assetBase} onCoverImage={onCoverImage}
        kicker="Service Plans Overview"
        subtitle="An overview of our managed IT support plans, so we can find the level of
                  cover that fits your business before we put a full proposal together."
      />
      {c.showHero !== false          && <HeroPage c={c} mode={mode} set={set} />}
      {c.showTierOverview !== false  && <TierOverviewPage c={c} />}
      {c.showFeatureMatrix !== false && <FeatureMatrixPages c={c} />}
      {c.showAddOns !== false        && <AddOnsPages c={c} />}
      {c.showTerms !== false         && <TermsPages c={c} />}
      {c.showResponseTimes !== false && <ResponseTimesPage c={c} />}
    </div>
  );
}
